import { HttpClient, HttpParams } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { IEnvironment } from 'app/projects/core/src/lib/interfaces/environment.interface';
import { MAXBRAIN_ENVIRONMENT } from 'app/projects/core/src/lib/services/environment.token';
import { IEntityApiService } from 'app/projects/entity/src/lib/interfaces/entity.service.interface';
import { EntityService } from 'app/projects/entity/src/lib/services/entity.service';
import { BehaviorSubject, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { IScheduleStreamContentActionPayload } from '../interfaces/schedule-stream-content.action-payload.interface';
import { IScheduledStreamContentAndTotalCount } from '../interfaces/scheduled-stream-content-paginated.query.interface';
import { IScheduledStreamContentQuery } from '../interfaces/scheduled-stream-content.query.interface';
import { CreateScheduledStreamContentCommand } from '../models/create-scheduled-stream-content.command.model';
import { ScheduledStreamContentCommand } from '../models/scheduled-stream-content.command.model';
import { MaxBrainScheduledStreamContent } from '../models/scheduled-stream-content.model';
import { IScheduleStreamContentBulkActionPayload } from '../interfaces/schedule-stream-content-bulk.action-payload.interface';
import { CreateScheduledStreamContentBulkCommand } from '../models/create-scheduled-stream-content-bulk.command.model';
import { MaxBrainScheduledStreamContentBulk } from '../models/scheduled-stream-content-bulk.model';
import { IScheduledStreamContentBulkQuery } from '../interfaces/scheduled-stream-content-bulk.query.interface';

@Injectable()
export class ScheduledStreamContentApiService
    extends EntityService<IScheduledStreamContentQuery, ScheduledStreamContentCommand>
    implements IEntityApiService<MaxBrainScheduledStreamContent>
{
    private _isThereMorePages = new BehaviorSubject<boolean>(false);
    public isThereMorePages$ = this._isThereMorePages.asObservable();

    constructor(http: HttpClient, @Inject(MAXBRAIN_ENVIRONMENT) environment: IEnvironment) {
        super(http, environment.apiUrl, 'engage/scheduled-stream-content');
    }

    updateIsThereMorePages(value: boolean) {
        this._isThereMorePages.next(value);
    }

    getAll(): Observable<MaxBrainScheduledStreamContent[]> {
        throw new Error('Method not implemented.');
    }

    getAllByAudienceAndStreamId(
        audienceId: string,
        featuredStreamId: string,
        pageSize = 5,
        pageIndex = 1,
        sortBy: string,
        sortOrder: string,
        search: string,
        selectedPostingTypeId: string
    ): Observable<IScheduledStreamContentAndTotalCount> {
        let params = new HttpParams()
            .set('page', pageIndex.toString())
            .set('limit', pageSize.toString())
            .set('filter[audience]', audienceId)
            .set('filter[featuredStream]', featuredStreamId);

        if (search !== undefined && search !== null) {
            params = params.set('filter[query]', search);
        }

        if (selectedPostingTypeId) {
            params = params.set('filter[type]', selectedPostingTypeId);
        }

        if (sortBy && sortOrder) {
            params = params.set('sortBy', sortBy);
            params = params.set('sortOrder', sortOrder);
        }

        return this.http.get<IScheduledStreamContentAndTotalCount>(`${this.apiUrl}/${this.entities}`, { params });
    }

    create(entity: MaxBrainScheduledStreamContent): Observable<MaxBrainScheduledStreamContent> {
        const data = new ScheduledStreamContentCommand(entity);

        return this._create(data).pipe(map((query) => new MaxBrainScheduledStreamContent(query)));
    }

    read(id: string): Observable<MaxBrainScheduledStreamContent> {
        return this._read(id).pipe(map((query) => new MaxBrainScheduledStreamContent(query)));
    }

    update(entity: MaxBrainScheduledStreamContent): Observable<MaxBrainScheduledStreamContent> {
        const data = new ScheduledStreamContentCommand(entity);

        return this._update(entity.id, data).pipe(map((query) => new MaxBrainScheduledStreamContent(query)));
    }

    delete(id: string): Observable<any> {
        return this._delete(id);
    }

    createScheduledStreamContent(payload: IScheduleStreamContentActionPayload): Observable<MaxBrainScheduledStreamContent> {
        const data = new CreateScheduledStreamContentCommand(payload);
        return this.http.post<IScheduledStreamContentQuery>(`${this.apiUrl}/engage/scheduled-stream-content`, data).pipe(map((query) => new MaxBrainScheduledStreamContent(query)));
    }

    createScheduledStreamContentBulk(payload: IScheduleStreamContentBulkActionPayload): Observable<MaxBrainScheduledStreamContentBulk> {
        const data = new CreateScheduledStreamContentBulkCommand(payload);
        return this.http
            .post<IScheduledStreamContentBulkQuery>(`${this.apiUrl}/engage/scheduled-stream-content/bulk`, data)
            .pipe(map((query) => new MaxBrainScheduledStreamContentBulk(query)));
    }

    updateScheduledStreamContent(payload: MaxBrainScheduledStreamContent): Observable<MaxBrainScheduledStreamContent> {
        const scssId = payload.id;
        const data = new CreateScheduledStreamContentCommand(payload);
        return this.http
            .patch<IScheduledStreamContentQuery>(`${this.apiUrl}/engage/scheduled-stream-content/${scssId}`, data)
            .pipe(map((query) => new MaxBrainScheduledStreamContent(query)));
    }
}
